import Link from "next/link"
import { ArrowRight, Check } from "lucide-react"

import { MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft, scaleIn } from "@/components/motion"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { AmbientGlow, LiftCard, MagneticCta } from "@/components/motion-primitives"

export type PremiumFaqItem = {
  question: string
  answer: string
}

export type PremiumFaqProps = {
  title?: string
  description?: string
  items: PremiumFaqItem[]
  className?: string
}

export type FinalCtaProps = {
  title: string
  description: string
  primaryCta: { label: string; href: string }
  supportingPoints?: string[]
  className?: string
}

export type PremiumFooterProps = {
  brand: string
  description?: string
  links?: { label: string; href: string }[]
  className?: string
}

/** Conversion sections for FAQs, final calls-to-action, and restrained footers. */
export function PremiumFaq({ title = "Questions worth answering", description, items, className }: PremiumFaqProps) {
  return (
    <MotionSection className={cn("bg-background py-24 sm:py-32", className)}>
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
        <MotionDiv variants={fadeInLeft}>
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
        </MotionDiv>
        <MotionList className="space-y-3">
          {items.map((item) => (
            <MotionDiv key={item.question} variants={blurIn}>
              <LiftCard lift={3} scale={1.01}>
                <Card className="h-full rounded-panel border-border/70 bg-card p-6 shadow-sm transition-all duration-500 hover:border-primary/30 hover:shadow-[0_20px_60px_hsl(var(--foreground)/0.08)]">
                  <h3 className="font-heading text-lg font-semibold tracking-tight text-foreground">{item.question}</h3>
                  <p className="mt-3 leading-7 text-muted-foreground">{item.answer}</p>
                </Card>
              </LiftCard>
            </MotionDiv>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}

export function FinalCta({ title, description, primaryCta, supportingPoints = [], className }: FinalCtaProps) {
  return (
    <MotionSection className={cn("relative isolate overflow-hidden bg-foreground py-20 text-background sm:py-28", className)}>
      <div className="absolute inset-0 -z-10 animate-gradient-shift bg-[radial-gradient(circle_at_18%_20%,hsl(var(--primary)/0.35),transparent_30%),radial-gradient(circle_at_85%_70%,hsl(var(--primary-glow)/0.18),transparent_28%)] bg-[length:140%_140%]" />
      <AmbientGlow className="left-[-12rem] top-1/2 h-[28rem] w-[28rem] -translate-y-1/2 bg-primary/24" duration={7.5} />
      <AmbientGlow className="right-[-10rem] bottom-[-4rem] h-[22rem] w-[22rem] bg-accent/20" duration={9} />
      <div className="mx-auto grid w-full max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_auto] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-6xl">{title}</h2>
          <p className="mt-5 text-lg leading-8 text-background/72">{description}</p>
          {supportingPoints.length > 0 && (
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {supportingPoints.map((point) => (
                <li key={point}>
                  <LiftCard lift={2}>
                    <div className="flex h-full items-start gap-3 rounded-card border border-background/10 bg-background/5 p-3 text-sm text-background/78">
                      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3.5" />
                      </span>
                      {point}
                    </div>
                  </LiftCard>
                </li>
              ))}
            </ul>
          )}
        </MotionDiv>
        <MotionDiv variants={scaleIn}>
          <Button asChild size="xl" className="group rounded-button bg-background text-foreground transition-transform duration-300 hover:scale-[1.03] hover:bg-background/90">
            <Link href={primaryCta.href}>
              {primaryCta.label}
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </MotionDiv>
      </div>
    </MotionSection>
  )
}

export function PremiumFooter({ brand, description, links = [], className }: PremiumFooterProps) {
  return (
    <footer className={cn("border-t border-border/70 bg-background py-10", className)}>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div className="max-w-md">
          <p className="font-heading text-lg font-semibold tracking-tight text-foreground">{brand}</p>
          {description && <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>}
        </div>
        {links.length > 0 && (
          <nav aria-label="Footer navigation" className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            {links.map((link) => (
              <Link key={link.href} href={link.href} className="transition-colors hover:text-foreground">
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </footer>
  )
}
